import dayjs from "dayjs";
import Auth from "./auth.service";
import { moduleName, actionName, actionUrl } from "../constants/defaultValues";

export const ucfirst = (str) => {
  if (typeof str !== "string") {
    return "";
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Title-Case a name: first letter of each word uppercase, the rest lowercase.
 * Collapses runs of whitespace and trims. Used by the shared name fields to
 * auto-format on blur (e.g. `priya verma` → `Priya Verma`).
 */
export const toTitleCase = (str) => {
  if (typeof str !== "string") {
    return "";
  }
  return str
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
};

/** Salutation options prepended into a customer's full name (FE-only, no DB column). */
export const SALUTATIONS = ["Mr.", "Mrs.", "Ms.", "Miss", "Mstr.", "Dr.", "Prof."];

/** Drop a leading known salutation token from a name (guards double-prefixing). */
export const stripSalutation = (name) => {
  if (typeof name !== "string") return "";
  const n = name.trim();
  for (const s of SALUTATIONS) {
    if (n.toLowerCase().startsWith(`${s.toLowerCase()} `)) return n.slice(s.length).trim();
  }
  return n;
};

/** Split a stored full name into { salutation, name } — used to prefill the edit form. */
export const splitSalutation = (fullName) => {
  if (typeof fullName !== "string") return { salutation: "", name: "" };
  const n = fullName.trim();
  for (const s of SALUTATIONS) {
    if (n.toLowerCase().startsWith(`${s.toLowerCase()} `)) {
      return { salutation: s, name: n.slice(s.length).trim() };
    }
  }
  return { salutation: "", name: n };
};

/**
 * Compose the stored full name from a picked salutation + typed name:
 * title-cases the name (stripping any salutation already in it), then prepends
 * the chosen salutation. e.g. ("Mrs.", "shetty yadav") → "Mrs. Shetty Yadav".
 */
export const composeCustomerName = (salutation, name) => {
  const base = toTitleCase(stripSalutation(name));
  const sal = (salutation || "").trim();
  return sal ? `${sal} ${base}`.trim() : base;
};

export const removeEmptyVals = (obj) => {
  return Object.keys(obj)
    .filter((key) => {
      const val = obj[key];

      // Remove null, undefined, and empty string
      if (val === null || val === undefined) return false;

      // If value is an array, remove if its length is 0
      if (Array.isArray(val) && val.length === 0) return false;

      return true;
    })
    .reduce((acc, k) => {
      acc[k] = obj[k];
      return acc;
    }, {});
};

export const formatDate = (dateString, formatString = "DD MMM YYYY") => {
  if (!dateString) {
    return "NA";
  }
  const date = dayjs(dateString).format(formatString);
  return date;
};

/**
 * Canonical date-time format for lists and detail views:
 * `DD/MM/YYYY, h:mm:ss AM/PM` (12-hour). Replaces ad-hoc toLocaleString().
 */
export const formatDateTime = (dateString) => {
  if (!dateString) {
    return "NA";
  }
  return dayjs(dateString).format("DD/MM/YYYY, h:mm:ss A");
};

export const calculateAge = (dateString) => {
  if (!dateString) {
    return "NA";
  }
  const birthDate = dayjs(dateString);
  const age = dayjs().diff(birthDate, "years");
  return age;
};

export const getGreeting = () => {
  const currentHour = dayjs().hour();

  if (currentHour >= 5 && currentHour < 12) {
    return "Good Morning";
  }
  if (currentHour >= 12 && currentHour < 16) {
    return "Good Afternoon";
  }
  return "Good Evening";
};

export const calculatePastMonths = (duration) => {
  const currentDate = dayjs(); // Current date

  const pastMonths = [];
  const startDate = currentDate.subtract(duration, "month");

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i <= duration; i++) {
    const month = startDate.add(i, "month").format("MMMM");
    pastMonths.push(month);
  }

  return pastMonths;
};

export function getFinancialYear() {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();

  if (month < 3) {
    return `${year - 1}-${year}`;
  } else {
    return `${year}-${year + 1}`;
  }
}

export function formatDateToDDMMYYYY(inputDate) {
  const possibleFormats = [
    "YYYY-MM-DD",
    "YYYY-MM-DDTHH:mm:ssZ",
    "MM/DD/YYYY",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "YYYY/MM/DD",
    "MMM D, YYYY",
    "MMMM D, YYYY",
  ];

  for (const format of possibleFormats) {
    const parsed = dayjs(inputDate, format, true);
    if (parsed.isValid()) {
      return parsed.format("DD MMM YYYY"); // e.g., "11 Mar 2025"
    }
  }

  // Fallback (same format: "11 Mar 2025")
  const fallback = new Date(inputDate);
  if (!isNaN(fallback.getTime())) {
    const day = String(fallback.getDate()).padStart(2, "0");
    const month = fallback.toLocaleString("default", { month: "short" }); // e.g., "Mar"
    const year = fallback.getFullYear();
    return `${day} ${month} ${year}`;
  }

  return "";
}

// ============================================================
// Permission helpers — DocAI portal pattern.
// Permission shape: { allowed, method, actionUrl, modules: { name } }
// ============================================================

/**
 * Check if user has access to (module, httpMethod) — ignores URL.
 * Returns true when module/method unspecified (open access).
 */
export const checkUserMethodAccess = (module, httpMethod) => {
  const userPermissions = Auth.userPermission || [];
  if (!module || !httpMethod) return true;

  return userPermissions.some(
    (permission) =>
      permission.allowed && permission.modules?.name === module && permission.method === httpMethod,
  );
};

/**
 * Check if user has access to (module, httpMethod, url).
 * Used by ProtectedRoute, sidebar items, and button gates.
 */
export const checkUserURLMethodAccess = (module, httpMethod, url) => {
  const userPermissions = Auth.userPermission || [];
  if (!module || !httpMethod) return true;

  return userPermissions.some(
    (permission) =>
      permission.allowed &&
      permission.modules?.name?.toUpperCase() === String(module).toUpperCase() &&
      permission.actionUrl === url &&
      permission.method === httpMethod,
  );
};

/**
 * Convenience: evaluate a mandatory + optional permission options object.
 * Same shape as ProtectedRoute's `options`.
 */
export const hasAccessByOptions = (options = {}) => {
  const { mandatoryPermissions = [], optionalPermissions = [], isAlwaysAllowed = false } = options;
  if (isAlwaysAllowed) return true;

  const hasMandatory =
    mandatoryPermissions.length === 0 ||
    mandatoryPermissions.every((p) => checkUserURLMethodAccess(p.module, p.method, p.actionUrl));

  const hasOptional =
    optionalPermissions.length === 0 ||
    optionalPermissions.some((p) => checkUserURLMethodAccess(p.module, p.method, p.actionUrl));

  return hasMandatory && hasOptional;
};

// ============================================================
// Permission-driven landing — DocGini getDashboardOrDefaultUrl pattern.
// Computes the post-login / post-store-change landing URL from the
// in-memory permission set. Runs only on fresh login / store change
// (never on reload — see auth provider synchronous user init).
// ============================================================

// Owner roles keep the Dashboard as home (detected by role string — the
// Dashboard is always-allowed and carries no permission of its own).
const OWNER_ROLES = new Set(["SUPER_ADMIN", "HO_MANAGER", "ACCOUNTANT"]);

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

// Fixed priority walk for non-owner roles: first path whose gating
// permission the user holds wins; Dashboard is the ultimate fallback.
const LANDING_PRIORITY = [
  { module: moduleName.POS, method: actionName.GET, url: actionUrl.POS_VIEW, path: "/pos" },
  { module: moduleName.GRN, method: actionName.GET, url: actionUrl.GRN_LIST, path: "/grn" },
  {
    module: moduleName.PRODUCTS,
    method: actionName.GET,
    url: actionUrl.PRODUCT_LIST,
    path: "/inventory/products",
  },
  {
    module: moduleName.CUSTOMER,
    method: actionName.GET,
    url: actionUrl.CUSTOMER_LIST,
    path: "/customers",
  },
  {
    module: moduleName.WHOLESALE,
    method: actionName.GET,
    url: actionUrl.INVOICE_LIST,
    path: "/multi-invoice",
  },
];

/**
 * First page the user can actually use, chosen from the permissions they
 * hold. Owner roles (Super Admin / HO Manager / Accountant) → Dashboard;
 * everyone else walks the fixed priority list; no match → Dashboard.
 */
export const getFirstPermittedUrl = () => {
  const role = normalizeRole(Auth.user?.role);
  if (OWNER_ROLES.has(role)) return "/";
  for (const entry of LANDING_PRIORITY) {
    if (checkUserURLMethodAccess(entry.module, entry.method, entry.url)) return entry.path;
  }
  return "/";
};
